import aiLogger from '../utils/aiLogger';

// Align token storage keys with App.js
const ACCESS_TOKEN_KEY = 'stratovai_access_token';
const REFRESH_TOKEN_KEY = 'stratovai_refresh_token';

const normalizeBaseUrl = (url) => {
  if (!url) {
    return '';
  }

  const trimmed = url.trim();
  if (!trimmed || trimmed === '/') {
    return '';
  }

  // Ensure hostname-only values get https:// so fetch() uses the right origin
  let base = trimmed;
  if (!/^https?:\/\//i.test(trimmed) && (trimmed.includes('railway.app') || trimmed.includes('netlify.app') || trimmed.includes('.'))) {
    base = `https://${trimmed}`;
  }

  // Drop trailing slash for consistency
  return base.endsWith('/') ? base.slice(0, -1) : base;
};

const getRuntimeBaseUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return (
    window.__STRATOVAI_API_BASE_URL__
    || window.STRATOVAI_API_BASE_URL
    || window.API_BASE_URL
    || ''
  );
};

const resolvedBaseUrl = normalizeBaseUrl(
  process.env.REACT_APP_API_BASE_URL
  || process.env.REACT_APP_API_URL
  || getRuntimeBaseUrl()
  || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000')
);

if (process.env.NODE_ENV === 'production') {
  if (!resolvedBaseUrl) {
    console.warn(
      '[API_CONFIG] Using relative API URLs in production. Set REACT_APP_API_BASE_URL (or REACT_APP_API_URL) if backend is on a different domain.'
    );
  } else {
    console.info('[API_CONFIG] API base:', resolvedBaseUrl);
  }
}

// API Configuration
const API_CONFIG = {
  // Base URL for API calls - resolved from env/runtime
  BASE_URL: resolvedBaseUrl,
  
  // API endpoints
  ENDPOINTS: {
    // Authentication
    LOGIN: '/auth/login',
    REFRESH_TOKEN: '/auth/refresh',
    LOGOUT: '/auth/logout',
    
    // Users
    USERS: '/users',
    // Projects for current user (backend route: GET /projects)
    PROJECTS: '/projects',
    
    // Projects
    PROJECT_SUMMARY: (projectId) => `/projects/${projectId}/summary`,
    PROJECT_LOGIN_CONFIG: (projectId) => `/projects/${projectId}/login-config`,
    PROJECT_EXPLORATION_RESULTS: (projectId) => `/projects/${projectId}/exploration-results`,
    PROJECT_TEST_CASES: (projectId, status) => `/projects/${projectId}/test-cases${status ? `?status=${status}` : ''}`,
    PROJECT_TEST_CASE_STATUS: (projectId, testCaseId) => `/projects/${projectId}/test-cases/${testCaseId}/status`,
    PROJECT_TEST_CASE_DELETE: (projectId, testCaseId) => `/projects/${projectId}/test-cases/${testCaseId}`,
    PROJECT_REFRESH_STATISTICS: (projectId) => `/projects/${projectId}/refresh-statistics`,
    
    // Test Cases
    TEST_CASES_APPROVE: '/test-cases/approve',
    TEST_CASES_IMPORT_EXPLORATION: '/test-cases/import-exploration-results',
    TEST_CASES_UPDATE_SOURCE: '/test-cases/update-source',
    
    // Orchestrator
    EXPLORE_UI: '/orchestrator/explore-ui',
    EXPLORE_UI_STATUS: (userId, projectId) =>
      `/orchestrator/explore-ui/status?user_id=${userId}&project_id=${projectId}`,
    AI_CHAT: '/orchestrator/ai-chat',
    GENERATE_AUTOMATION: '/orchestrator/generate-automation',
    RUN_AUTOMATION: '/orchestrator/run-automation',
    
    // Automation Integration (New Bulletproof System)
    GENERATE_AUTOMATION_FROM_EXPLORER: (projectId) => `/automation-integration/generate-from-explorer/${projectId}`,
    GENERATE_AUTOMATION_FROM_TEST_CASES: (projectId) => `/automation-integration/generate-from-test-cases/${projectId}`,
    GENERATE_UNIFIED_AUTOMATION: (projectId) => `/automation-integration/generate-unified/${projectId}`,
    AUTOMATION_STATUS: (projectId) => `/automation-integration/status/${projectId}`,
    
    // Universal Learning Explorer
    START_UNIVERSAL_EXPLORATION: '/universal-exploration/start-universal-exploration',
    UNIVERSAL_EXPLORATION_STATUS: (sessionId) => `/universal-exploration/universal-exploration-status/${sessionId}`,
    UNIVERSAL_EXPLORATION_RESULTS: (sessionId) => `/universal-exploration/universal-exploration-results/${sessionId}`,
    
    // Zo's Integration Endpoints
    EXPLORER_SYNTHESIZE: (sessionId) => `/explorer/${sessionId}/synthesize`,
    EXPLORER_GENERATE_AUTOMATION: (sessionId) => `/explorer/${sessionId}/generate-automation`,

    // Quality Insights
    QUALITY_HEATMAP: '/quality/heatmap',
    QUALITY_TREND: '/quality/trend',
    QUALITY_MTTR: '/quality/mttr',
    QUALITY_METADATA: '/quality/metadata',
    QUALITY_BATCHES: '/quality/admin/batches',
    
    // Automation Test Cases endpoints
    AUTOMATION_TEST_CASES: (projectId) => `/automation/test-cases?project_id=${projectId}`,
    AUTOMATION_GENERATE: '/automation/generate',
    AUTOMATION_RUN: '/automation/run',
    AUTOMATION_ARTIFACTS: (testCaseId) => `/automation/artifacts/${testCaseId}`,
    AUTOMATION_UPLOAD: '/automation/upload',

    // Product Knowledge Base (canonical paths; avoid string duplication in callers)
    PKB_ANALYZE_REQUIREMENT: (projectId) => `/pkb/${projectId}/analyze-requirement`,
    
    // Test Case Generation endpoints
    GENERATE_TEST_CASES: (projectId, storyKey) => `/api/projects/${projectId}/stories/${storyKey}/test-cases/generate`,
    BULK_SAVE_TEST_CASES: (projectId) => `/api/projects/${projectId}/test-cases/bulk-save`,
  },
  
  /**
   * Base headers for API calls.
   * @param {string|null} token - Bearer token, or null
   * @param {{ json?: boolean }} [opts] - When json is false, omit Content-Type (e.g. before FormData upload)
   */
  getHeaders: (token = null, opts = {}) => {
    const useJson = opts.json !== false;
    const headers = {};
    if (useJson) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  },
  
  // Helper function to build full URL
  buildUrl: (endpoint) => {
    if (!endpoint) {
      return API_CONFIG.BASE_URL || '';
    }

    if (/^https?:\/\//i.test(endpoint)) {
      return endpoint;
    }

    const base = API_CONFIG.BASE_URL || '';
    if (!base) {
      return endpoint;
    }

    if (base.endsWith('/') && endpoint.startsWith('/')) {
      return `${base}${endpoint.slice(1)}`;
    }

    if (!base.endsWith('/') && !endpoint.startsWith('/')) {
      return `${base}/${endpoint}`;
    }

    return `${base}${endpoint}`;
  },
  
  /**
   * Auth-aware fetch: attaches Bearer token, optional JSON Content-Type, retries once on 401 after refresh.
   * FormData bodies must not set Content-Type (browser sets multipart boundary).
   */
  async request(endpoint, options = {}) {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const url = this.buildUrl(endpoint);
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

    const headers = {
      ...this.getHeaders(token, { json: !isFormData }),
      ...options.headers,
    };
    if (isFormData) {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }

    const config = { ...options, headers };
    let response = await fetch(url, config);

    if (response.status === 401) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        const newToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        const retryHeaders = {
          ...this.getHeaders(newToken, { json: !isFormData }),
          ...options.headers,
        };
        if (isFormData) {
          delete retryHeaders['Content-Type'];
          delete retryHeaders['content-type'];
        }
        response = await fetch(url, { ...options, headers: retryHeaders });
      }
    }

    return response;
  },
  
  // Refresh token helper
  async refreshToken() {
    try {
      // Use the same refresh token key as App.js
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) return false;
      
      const response = await fetch(this.buildUrl(this.ENDPOINTS.REFRESH_TOKEN), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Persist the new access token under the aligned key
        localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  },

  // Add automation API methods
  async generateAutomation(projectId, userId) {
    const response = await this.request(this.ENDPOINTS.GENERATE_AUTOMATION, {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId, user_id: userId })
    });
    return response.json();
  },

  async runAutomation(projectId, userId) {
    const response = await this.request(this.ENDPOINTS.RUN_AUTOMATION, {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId, user_id: userId })
    });
    return response.json();
  },

  async refreshProjectStatistics(projectId) {
    const response = await this.request(this.ENDPOINTS.PROJECT_REFRESH_STATISTICS(projectId), {
      method: 'POST'
    });
    return response.json();
  },

  async deleteTestCase(projectId, testCaseId) {
    const response = await this.request(this.ENDPOINTS.PROJECT_TEST_CASE_DELETE(projectId, testCaseId), {
      method: 'DELETE'
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  },

  async getExplorationStatus(userId, projectId) {
    try {
      aiLogger.debug('EXPLORE_UI_STATUS called with:', { userId, projectId });
      const response = await this.request(this.ENDPOINTS.EXPLORE_UI_STATUS(userId, projectId), {
        method: 'GET',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      aiLogger.error('Failed to get exploration status:', error);
      throw error;
    }
  },

  // Universal Learning Explorer methods
  async startUniversalExploration(userId, projectId, configDir = "config") {
    try {
      const response = await this.request(this.ENDPOINTS.START_UNIVERSAL_EXPLORATION, {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          project_id: projectId,
          config_dir: configDir
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error starting Universal Learning Explorer:', error);
      throw error;
    }
  },

  async getUniversalExplorationStatus(sessionId) {
    try {
      const response = await this.request(this.ENDPOINTS.UNIVERSAL_EXPLORATION_STATUS(sessionId), {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error getting Universal Learning Explorer status:', error);
      throw error;
    }
  },

  async getUniversalExplorationResults(sessionId) {
    try {
      const response = await this.request(this.ENDPOINTS.UNIVERSAL_EXPLORATION_RESULTS(sessionId), {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error getting Universal Learning Explorer results:', error);
      throw error;
    }
  },

  // New Bulletproof Automation Integration Methods
  async generateAutomationFromExplorer(projectId, explorationResults) {
    try {
      aiLogger.info(`🚀 Generating automation from explorer for project ${projectId}`);
      
      const response = await this.request(this.ENDPOINTS.GENERATE_AUTOMATION_FROM_EXPLORER(projectId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exploration_results: explorationResults
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      aiLogger.success(`✅ Automation generation from explorer completed:`, result);
      return result;
    } catch (error) {
      aiLogger.error('❌ Error generating automation from explorer:', error);
      throw error;
    }
  },

  async generateAutomationFromTestCases(projectId, testCases) {
    try {
      aiLogger.info(`🚀 Generating automation from test cases for project ${projectId}`);
      
      const response = await this.request(this.ENDPOINTS.GENERATE_AUTOMATION_FROM_TEST_CASES(projectId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          test_cases: testCases
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      aiLogger.success(`✅ Automation generation from test cases completed:`, result);
      return result;
    } catch (error) {
      aiLogger.error('❌ Error generating automation from test cases:', error);
      throw error;
    }
  },

  async generateUnifiedAutomation(projectId, explorationResults, testCases) {
    try {
      aiLogger.info(`🚀 Generating unified automation for project ${projectId}`);
      
      const response = await this.request(this.ENDPOINTS.GENERATE_UNIFIED_AUTOMATION(projectId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exploration_results: explorationResults,
          test_cases: testCases
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      aiLogger.success(`✅ Unified automation generation completed:`, result);
      return result;
    } catch (error) {
      aiLogger.error('❌ Error generating unified automation:', error);
      throw error;
    }
  },

  async getAutomationStatus(projectId) {
    try {
      const response = await this.request(this.ENDPOINTS.AUTOMATION_STATUS(projectId), {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error getting automation status:', error);
      throw error;
    }
  },

  // Quality Insights API methods
  async getQualityHeatmap(projectId, { since, allowLong } = {}) {
    const params = new URLSearchParams({ project_id: projectId });
    if (since) params.set('since', since);
    if (allowLong) params.set('allow_long', 'true');

    const response = await this.request(`${this.ENDPOINTS.QUALITY_HEATMAP}?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async getQualityTrend(projectId, { since, allowLong } = {}) {
    const params = new URLSearchParams({ project_id: projectId });
    if (since) params.set('since', since);
    if (allowLong) params.set('allow_long', 'true');

    const response = await this.request(`${this.ENDPOINTS.QUALITY_TREND}?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async getQualityMttr(projectId) {
    const params = new URLSearchParams({ project_id: projectId });
    const response = await this.request(`${this.ENDPOINTS.QUALITY_MTTR}?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async getQualityMetadata(projectId) {
    const params = new URLSearchParams({ project_id: projectId });
    const response = await this.request(`${this.ENDPOINTS.QUALITY_METADATA}?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async getQualityBatches(projectId, { limit = 50 } = {}) {
    const params = new URLSearchParams({ project_id: projectId, limit });
    const response = await this.request(`${this.ENDPOINTS.QUALITY_BATCHES}?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  // Automation Test Cases API methods
  async getAutomationTestCases(projectId, filters = {}) {
    try {
      const queryParams = new URLSearchParams({
        project_id: projectId,
        ...filters
      });
      
      const response = await this.request(`/automation/test-cases?${queryParams}`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error getting automation test cases:', error);
      throw error;
    }
  },

  // Upload regular Test Cases (not automation)
  async uploadTestCases(projectId, file, format, title = null) {
    try {
      const formData = new FormData();
      formData.append('project_id', String(projectId));
      formData.append('format', String(format));
      if (title) formData.append('title', String(title));
      formData.append('file', file);

      const response = await this.request('/tests/upload', { method: 'POST', body: formData });
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.json();
          errorMessage = errorBody?.detail || errorBody?.message || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error uploading test cases:', error);
      throw error;
    }
  },

  async generateAutomationFromTestCase(testCaseId, options = {}) {
    try {
      const response = await this.request(this.ENDPOINTS.AUTOMATION_GENERATE, {
        method: 'POST',
        body: JSON.stringify({
          automation_tc_id: testCaseId,
          save: options.save || false
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error generating automation from test case:', error);
      throw error;
    }
  },

  async runAutomationTestCase(testCaseId, options = {}) {
    try {
      const response = await this.request(this.ENDPOINTS.AUTOMATION_RUN, {
        method: 'POST',
        body: JSON.stringify({
          automation_tc_id: testCaseId,
          mode: options.mode || 'headed'
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error running automation test case:', error);
      throw error;
    }
  },

  async getAutomationArtifacts(testCaseId) {
    try {
      const response = await this.request(this.ENDPOINTS.AUTOMATION_ARTIFACTS(testCaseId), {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error getting automation artifacts:', error);
      throw error;
    }
  },

  async downloadAutomationArtifacts(testCaseId) {
    try {
      const response = await this.request(`${this.ENDPOINTS.AUTOMATION_ARTIFACTS(testCaseId)}/download`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.blob();
    } catch (error) {
      aiLogger.error('❌ Error downloading automation artifacts:', error);
      throw error;
    }
  },

  async uploadAutomationTestCases(projectId, file, format, createdBy = 'system', title = null) {
    try {
      const formData = new FormData();
      formData.append('project_id', String(projectId));
      formData.append('format', String(format));
      formData.append('created_by', String(createdBy));
      if (title) {
        formData.append('title', String(title));
      }
      // Include original filename so backend can retain it as test case title when needed
      if (file && file.name) {
        formData.append('file_name', file.name);
      }
      formData.append('file', file);

      const response = await this.request(this.ENDPOINTS.AUTOMATION_UPLOAD, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.json();
          errorMessage = errorBody?.detail || errorBody?.message || errorMessage;
        } catch {
          /* non-JSON error body */
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      aiLogger.error('❌ Error uploading automation test cases:', error);
      throw error;
    }
  },
};

// Create an instance of the API client
const apiClient = API_CONFIG;

export default API_CONFIG;
export { apiClient }; 